<template>
  <div class="insights-unusual-activity-widget-table">
    <AppTable
      :key="isUserDashboard"
      ref="table"
      hoverable
      :loading="loading"
      :selectable="false"
      :table-data="tableData"
      :rows-count="rowsCount"
      alt-field="deactivated"
      td-classes="cursor-pointer"
      @sort="sort"
      @clickRow="handleClick"
    >
      <template v-if="isUserDashboard">
        <AppTableColumn
          v-slot="{ row }"
          header="Date"
          name="date"
          :sort-by="buildSortOptions('date')"
        >
          {{ formatDate(row.date) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Instances"
          name="instances"
          :sort-by="buildSortOptions('instances')"
        >
          {{ row.instances }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Total time"
          name="duration_in_minutes"
          :sort-by="buildSortOptions('duration_in_minutes')"
        >
          {{ formatDuration(parseInt(row.duration_in_minutes)) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Highest classification"
          :header-tooltip="INSIGHTS.tooltips.confidenceLevel.content"
          header-tooltip-type="info-filled"
          name="confidence_level"
          :sort-by="buildSortOptions('confidence_level')"
        >
          <InsightsUnusualActivityConfidenceBadge :confidence-level="row.confidence_level" />
        </AppTableColumn>
      </template>
      <template v-else>
        <AppTableColumn
          v-slot="{ row }"
          header="Top members"
          name="name"
          :sort-by="buildSortOptions('name')"
        >
          <AppAvatar
            size="speck"
            :avatar="row.avatar"
          />
          <span>{{ row.name }}</span>
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Total time"
          name="total_duration"
          :sort-by="buildSortOptions('total_duration')"
        >
          {{ formatDuration(row.total_duration) }}
        </AppTableColumn>
        <AppTableColumn
          v-slot="{ row }"
          header="Highest classification"
          header-classes="no-wrap"
          :header-tooltip="INSIGHTS.tooltips.confidenceLevel.content"
          header-tooltip-type="info-filled"
          name="confidence_level"
          :sort-by="buildSortOptions('confidence_level')"
        >
          <InsightsUnusualActivityConfidenceBadge :confidence-level="row.confidence_level" />
        </AppTableColumn>
      </template>
    </AppTable>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { INSIGHTS } from '~/app_constants';
import { kebabCase } from 'lodash-es';
import AppAvatar from '~/components/AppAvatar.vue';
import moment from 'moment-timezone';
import { formatDate, secondsToHours } from '~/utils/time_utils';
import InsightsUnusualActivityConfidenceBadge from '~/components/insights/InsightsUnusualActivityConfidenceBadge.vue';

export default {
  name: 'InsightsUnusualActivityWidgetHighlightsTable',
  components: {
    InsightsUnusualActivityConfidenceBadge,
    AppAvatar,
  },
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: true,
    },
    sortOptions: {
      type: Object,
      required: true,
    },
    timeZone: {
      type: String,
      required: true,
    },
    widgetData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data: () => ({
    tooltipOptions: Object.freeze({ placement: 'top' }),
    memberRemovedTooltip: 'This member has been removed from the organization',
  }),
  computed: {
    INSIGHTS() {
      return INSIGHTS
    },
    ...mapGetters(['isUserDashboard', 'organizationId']),
    isParentWidgetComponent() {
      return (
        this.$parent?.$parent?.widgetKey === 'unusual_activities_list' ||
        this.$parent?.$parent?.widgetKey === 'unusual_user_activities'
      );
    },
    tableData() {
      return this.isUserDashboard ? this.userTableData : this.teamTableData;
    },
    rowsCount() {
      const defaultCount = this.isParentWidgetComponent ? 'unusualActivityLimit' : 'unusualActivityViewMoreLimit';

      return this.tableData?.length || INSIGHTS.parameters[defaultCount];
    },
    userTableData() {
      const data = this.widgetData?.unusual_activities || {};
      const userTableData = Object.values(data).slice(0, 5);

      return userTableData;
    },
    teamTableData() {
      return this.widgetData?.users?.map((member) => {
        const memberTotalInstances = member.stats.instances;

        return {
          ...member,
          unusual_activities: {
            stats: memberTotalInstances,
            list: Object.entries(member.stats).map(([key, value]) => ({
              key: kebabCase(key),
              value: this.calculatePercentage(value, memberTotalInstances),
              tooltip: INSIGHTS.unusualActivity.labels[kebabCase(key)]?.text,
            })),
          },
        };
      });
    },
  },
  methods: {
    buildSortOptions(field) {
      return {
        field,
        direction: this.sortOptions?.field === field ? this.sortOptions.direction : null,
      };
    },
    calculatePercentage(value, total) {
      if (!total) {
        return 0;
      }

      return Math.round(((value * 100) / total) * 10) / 10;
    },
    formatDate(date) {
      return formatDate(moment(date));
    },
    formatDuration(minutes) {
      return minutes ? secondsToHours((minutes || 0) * 60) : '—';
    },
    goToDay(date) {
      const url = new URL(window.location.href);

      url.searchParams.set('tab', 'unusual-activity');
      url.searchParams.set('from', date);
      url.searchParams.set('to', date);
      window.location.href = url.toString();
    },
    goToMember(user) {
      const url = new URL(window.location.href);

      url.searchParams.set('tab', 'unusual-activity');
      url.searchParams.set('user_id', user.id);

      window.location.href = url.toString();
    },
    handleClick(row) {
      window.analytics?.track('Unusual instance selected', {
        statsigCustomIDs: ['organizationID', this.organizationId],
      });
      this.isUserDashboard ? this.goToDay(row.date) : this.goToMember(row);
    },
    kebabCase(key) {
      return kebabCase(key);
    },
    sort(options) {
      this.$emit('sort', options);
    },
    switchMember(user) {
      this.$emit('switchMember', user.id);
    },
  },
};
</script>
